/* eslint-disable react/prop-types */
import {useContext, useState} from 'react'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import MoleculeRadioButtonField from '@s-ui/react-molecule-radio-button-field'
import {useLocation, useRouter} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'
import LayoutPageContent from '@adv-mt/mt-layout-page-content'
import ModalTransactionCancel from '@adv-mt/mt-modal-transaction-cancel'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'
import IconAngleLeft from '@adv-mt/mt-svg-icons/lib/AngleLeft.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {ROUTES} from '../../../config/index.js'
import TransactionTrustFooter from '../components/transactionTrustFooter.js'
import {
  INPUT_FIELD_ADDRESS_ID,
  INPUT_FIELD_CITY_ID,
  INPUT_FIELD_EMAIL_ID,
  INPUT_FIELD_LAST_NAME_ID,
  INPUT_FIELD_NAME_ID,
  INPUT_FIELD_PERSONAL_ID_ID,
  INPUT_FIELD_PHONE_ID,
  INPUT_FIELD_ZIP_CODE_ID
} from '../config.js'
import HelpText from './components/HelpText.js'
import {getInitialProps} from './getInitialProps.js'

const TransactionBuyerProfession = ({adPrice, adSellerId, adTitle, professionalActivitiesList = {}, __HTTP__ = {}}) => {
  const {redirectTo} = __HTTP__

  const {domain, i18n, user} = useContext(Context)
  const userData = user || {}
  const {pathname, search, state, query} = useLocation()
  const {push, replace} = useRouter()
  const [showGenericError, setShowGenericError] = useState(false)
  const [showNoSelectionError, setShowNoSelectionError] = useState(false)
  const [selectedProfessionalActivity, setSelectedProfessionalActivity] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [showModalCancel, setShowModalCancel] = useState(false)
  const {track} = useAnalyticsTrack()
  const {adId, tradeId} = query

  const {professionalActivities = []} = professionalActivitiesList

  if (redirectTo) {
    push(redirectTo)
    return <></>
  }

  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const {AlreadyCancelledTradeTransactionsError} = config.get('COMMON').ERRORS

  const {TRACKING_EVENT_NAMES} = config.get('tracking')

  const {META_ROBOTS_NO_INDEX_NO_FOLLOW} = config.get('seo')
  const helmet = {
    meta: [META_ROBOTS_NO_INDEX_NO_FOLLOW]
  }

  const handleOnModalClose = () => {
    setShowModalCancel(false)
  }

  const handleOnClickCancelCta = () => {
    setShowModalCancel(false)
  }

  const handleOnClickTopbarCta = async () => {
    setShowModalCancel(true)
  }

  const topbarCtaText = i18n.t('COMMON.CANCEL_PURCHASE')

  const handleOnClickCta = async () => {
    setIsLoading(true)
    setShowGenericError(false)
    setShowNoSelectionError(false)

    if (!selectedProfessionalActivity) {
      setIsLoading(false)
      setShowNoSelectionError(true)

      domain
        .get('get_event_tracking_data_use_case')
        .execute({
          event: TRACKING_EVENT_NAMES.VALIDATION_BUYER_IDENTITY_ADD_PROFESSIONAL_ACTIVITY_ERROR_TRANSACTION,
          ad: {id: adId},
          professionalActivityCode: selectedProfessionalActivity
        })
        .then(eventTrackingData => {
          track(eventTrackingData)
        })
    } else {
      const {buyerFormData = {}} = state || {}

      const buyerData = {
        address: buyerFormData[INPUT_FIELD_ADDRESS_ID],
        city: buyerFormData[INPUT_FIELD_CITY_ID],
        email: buyerFormData[INPUT_FIELD_EMAIL_ID],
        lastName: buyerFormData[INPUT_FIELD_LAST_NAME_ID],
        name: buyerFormData[INPUT_FIELD_NAME_ID],
        personalId: buyerFormData[INPUT_FIELD_PERSONAL_ID_ID],
        phone: buyerFormData[INPUT_FIELD_PHONE_ID],
        professionalActivityCode: selectedProfessionalActivity,
        tradeId,
        zipCode: buyerFormData[INPUT_FIELD_ZIP_CODE_ID]
      }

      const [error] = await domain.get('update_buyer_trade_data_transactions_use_case').execute({tradeId, ...buyerData})

      if (error instanceof AlreadyCancelledTradeTransactionsError) {
        push(ROUTES.USER.MESSAGES)
        return
      }

      if (error) {
        setShowGenericError(true)
        setIsLoading(false)
        return
      }

      domain
        .get('get_event_tracking_data_use_case')
        .execute({
          event: TRACKING_EVENT_NAMES.VALIDATION_BUYER_IDENTITY_ADD_PROFESSIONAL_ACTIVITY_CLICKED_TRANSACTION,
          ad: {id: adId},
          professionalActivityCode: selectedProfessionalActivity
        })
        .then(eventTrackingData => {
          track(eventTrackingData)
        })

      const newState = {showTransactionBuyerIdentityModal: true, ...buyerData}

      const [conversationIdError, response] = await domain
        .get('get_messaging_conversation_id_user_use_case')
        .execute({entityId: adId, buyerId: userData?.userId, sellerId: adSellerId})

      if (conversationIdError !== null) {
        replace({
          pathname: ROUTES.USER.MESSAGES,
          state: newState
        })
      }
      replace({
        pathname: `/user/messages/#/id/${response.conversationId}`,
        state: newState
      })
    }
  }

  return (
    <NavigationRequireAuth currentPath={`${pathname}${search}`}>
      <Helmet {...helmet} />
      <Layout.FullWidth
        linkFactory={LinkFactory}
        footerProps={{isSimple: true}}
        topbarProps={{
          customContent: (
            <AtomButton
              color={atomButtonColors.PRIMARY}
              design={atomButtonDesigns.SOLID}
              disabled={false}
              onClick={handleOnClickTopbarCta}
              shape={atomButtonShapes.CIRCULAR}
              size={isMobile ? atomButtonSizes.SMALL : atomButtonSizes.MEDIUM}
            >
              {topbarCtaText}
            </AtomButton>
          ),
          mainNavigationItems: [],
          navButton: (
            <AtomButton
              color="primary"
              design={atomButtonDesigns.FLAT}
              leftIcon={<IconAngleLeft />}
              link
              onClick={() => window.history.back()}
              size={atomButtonSizes.LARGE}
              shape={atomButtonShapes.CIRCULAR}
            />
          ),
          showBrandIcon: true,
          showNavUserOnDesktop: false,
          showToggleNavOnMobile: false,
          ...(!isMobile && {title: `${i18n.t('COMMON.SECURE_PURCHASE_OF')} ${adTitle}`})
        }}
      >
        <Layout.Container isFullHeight isFullWidth isMarginless>
          <LayoutPageContent
            buttonsConfig={{
              primaryCta: {
                color: atomButtonColors.ACCENT,
                isLoading,
                design: atomButtonDesigns.SOLID,
                handleOnClick: handleOnClickCta,
                text: i18n.t('VEHICLE_TRANSACTION.BUYER_PROFESSION.CTA')
              }
            }}
            footer={<TransactionTrustFooter />}
            helpBox={<HelpText />}
            stepperConfig={{
              steps: 5,
              step: 3,
              labels: ['', '', i18n.t('VEHICLE_TRANSACTION.BUYER_PROFESSION.STEP'), '', '']
            }}
            subtitle={i18n.t('VEHICLE_TRANSACTION.BUYER_PROFESSION.TITLE')}
          >
            <div className="mt-TransactionBuyerProfession-mainContent">
              {professionalActivities.map(professionalActivity => {
                const {id: professionalActivityId, description: professionalActivityDescription} = professionalActivity

                return (
                  <div className="mt-TransactionBuyerProfession-mainContentOptionWrapper">
                    <MoleculeRadioButtonField
                      fullWidth
                      id={professionalActivityId}
                      key={professionalActivityId}
                      name={professionalActivityId}
                      nodeLabel={
                        <div className="mt-TransactionBuyerProfession-mainContentOptionLabel">
                          {professionalActivityDescription}
                        </div>
                      }
                      onClick={e => {
                        setSelectedProfessionalActivity(e.target.value)
                      }}
                      checked={selectedProfessionalActivity === professionalActivityId}
                      value={professionalActivityId}
                    />
                  </div>
                )
              })}
              {showGenericError && (
                <div className="mt-TransactionBuyerProfession-errorText">
                  {i18n.t('ERROR_MESSAGE_SERVER_ERROR_COCHES_TEXT')}
                </div>
              )}
              {showNoSelectionError && (
                <div className="mt-TransactionBuyerProfession-errorText">
                  {i18n.t('VEHICLE_TRANSACTION.BUYER_PROFESSION.FIELD_ERROR_MESSAGES.MANDATORY_PROFESSIONAL_ACTIVITY')}
                </div>
              )}
            </div>
          </LayoutPageContent>
          {showModalCancel && (
            <ModalTransactionCancel
              onCtaClick={handleOnClickCancelCta}
              onModalClose={handleOnModalClose}
              targetPath={ROUTES.USER.MESSAGES}
            />
          )}
        </Layout.Container>
      </Layout.FullWidth>
    </NavigationRequireAuth>
  )
}

TransactionBuyerProfession.displayName = 'TransactionBuyerProfession'
TransactionBuyerProfession.propTypes = {}

TransactionBuyerProfession.getInitialProps = getInitialProps

export default TransactionBuyerProfession
